import './index.scss';
import './step1.scss';
import './step2.scss';
import './step3.scss';

import './checkbox.scss';
import './select.scss';

import Accordion from '../accordion/';

const SELECTOR_ROOT = '[data-safety-briefing="1"][data-safety-briefing-ce]';

// Track context across the page so Step 3 forms rendered outside the CE wrapper
// can still be filled correctly (RECORDS rendering etc.).
const contextByLang = new Map();
let globalListenersInstalled = false;

function isSameOriginUrl(url) {
    try {
        const u = new URL(url, window.location.href);
        return u.origin === window.location.origin;
    } catch (e) {
        return false;
    }
}

function parseHtml(html) {
    const parser = new DOMParser();
    return parser.parseFromString(html, 'text/html');
}

function tryAttachFriendlyCaptcha(roots, attempt = 0) {
    if (!roots || roots.length <= 0) {
        return;
    }
    try {
        // v0.1.x site compat SDK exposes `window.frcaptcha` with `.attach(NodeList|Array)`
        // (this is what your current sdk@0.1.26-site.compat.min.js provides).
        const frc = window.frcaptcha;
        if (frc && typeof frc.attach === 'function') {
            frc.attach(roots);
            return;
        }
    } catch (e) {
        // ignore
    }

    // SDK may not be loaded yet (scripts are in footer). Retry briefly.
    if (attempt < 10) {
        window.setTimeout(() => tryAttachFriendlyCaptcha(roots, attempt + 1), 200);
    }
}

function getRootCeId(root) {
    const v = root.getAttribute('data-safety-briefing-ce') || '';
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function normalizeLanguageIso(value) {
    if (!value) {
        return '';
    }
    const raw = String(value).trim().toLowerCase().replace('_', '-');
    if (!raw) {
        return '';
    }
    return raw.split('-')[0] || '';
}

function applyCaptchaLanguage(forms, safetyLang) {
    const lang = normalizeLanguageIso(safetyLang);
    if (!lang) {
        return;
    }
    forms.forEach((form) => {
        try {
            form.querySelectorAll('.frc-captcha').forEach((el) => {
                // FriendlyCaptcha reads language from data-lang at widget init.
                el.setAttribute('data-lang', lang);
            });
        } catch (e) {
            // ignore
        }
    });
}

function findMatchingRoot(doc, ceId) {
    return doc.querySelector(`${SELECTOR_ROOT}[data-safety-briefing-ce="${ceId}"]`);
}

function replaceRoot(currentRoot, nextRoot) {
    if (!currentRoot || !nextRoot) {
        return null;
    }
    currentRoot.replaceWith(nextRoot);
    return nextRoot;
}

function openAccordionItem(item) {
    if (!item || !(item instanceof Element)) {
        return;
    }
    if (item.classList.contains('opened')) {
        return;
    }
    const pane = item.querySelector('.accordion-item__pane');
    item.classList.add('opened');
    if (pane) {
        try {
            pane.style.maxHeight = pane.scrollHeight + 'px';
        } catch (e) {
            // ignore
        }
    }
}

function getStep2ActiveSectionUidFromEventTarget(target) {
    if (!target || !(target instanceof Element)) {
        return 0;
    }
    // Step 2 checkboxes use the section uid as value.
    const cb = target.closest('input[name="sb_confirmed_sections[]"]');
    if (!cb) {
        return 0;
    }
    const v = cb.getAttribute('value') || '';
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function captureAnchorPosition(root, sectionUid) {
    if (!root || !sectionUid) {
        return null;
    }
    const input = root.querySelector(`.wf-safetybriefing__step-2 input[name="sb_confirmed_sections[]"][value="${sectionUid}"]`);
    if (!input) {
        return null;
    }
    const item = input.closest('.accordion-item') || null;
    const link = item ? item.querySelector('.accordion-item__link') : null;
    const anchor = link || item || input;
    if (!anchor) {
        return null;
    }
    const rect = anchor.getBoundingClientRect();
    return { sectionUid, viewportTop: rect.top };
}

function restoreAnchorPositionAndOpen(root, anchor) {
    if (!root || !anchor || !anchor.sectionUid) {
        return;
    }
    const input = root.querySelector(`.wf-safetybriefing__step-2 input[name="sb_confirmed_sections[]"][value="${anchor.sectionUid}"]`);
    if (!input) {
        return;
    }
    const item = input.closest('.accordion-item') || null;
    if (item) {
        openAccordionItem(item);
    }
    // Keep the item roughly at the same viewport position as before the swap.
    try {
        const link = item ? item.querySelector('.accordion-item__link') : null;
        const anchorEl = link || item || input;
        const rect = anchorEl.getBoundingClientRect();
        const delta = rect.top - anchor.viewportTop;
        if (Number.isFinite(delta) && Math.abs(delta) > 1) {
            window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
        }
    } catch (e) {
        // ignore
    }
}

function scrollToStep3Headline(root) {
    if (!root) {
        return;
    }
    try {
        const step3 = root.querySelector('.wf-safetybriefing__step-3');
        if (!step3) {
            return;
        }

        // Prefer the stable anchor on the section itself.
        if (step3.id) {
            const url = new URL(window.location.href);
            url.hash = step3.id;
            window.history.replaceState(null, '', url.toString());
        }

        const h2 = step3.querySelector('.wf-safetybriefing__step__header h2');
        const target = h2 || step3;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
        // ignore
    }
}

function serializeFormSubmit(form, submitter) {
    const formData = new FormData(form);

    // Include clicked button name/value (important for sb_mark_complete).
    if (submitter && submitter.name) {
        formData.set(submitter.name, submitter.value || '1');
    }

    return formData;
}

function readFormSafetyLang(form) {
    try {
        const input = form.querySelector('input[name*="[safety_lang]"]');
        return input && input.value ? String(input.value).trim() : '';
    } catch (e) {
        return '';
    }
}

function resolveContextCeIdForForm(form, fallbackRoot) {
    // 1) Prefer the closest Safety Briefing wrapper
    const sbRoot = form.closest(SELECTOR_ROOT) || fallbackRoot || null;
    if (sbRoot) {
        const sbCe = sbRoot.getAttribute('data-safety-briefing-ce') || '';
        if (sbCe && sbCe !== '0') {
            return sbCe;
        }
    }

    // 2) Try language-based mapping
    const lang = readFormSafetyLang(form);
    if (lang && contextByLang.has(lang)) {
        return contextByLang.get(lang) || '';
    }

    // 3) As a last resort: if there is exactly one Safety Briefing on page, use it
    const all = document.querySelectorAll(SELECTOR_ROOT);
    if (all.length === 1) {
        const only = all[0];
        const sbCe = only.getAttribute('data-safety-briefing-ce') || '';
        return sbCe && sbCe !== '0' ? sbCe : '';
    }

    return '';
}

function fillStep3ContextFields(sbRoot) {
    if (!sbRoot) {
        return;
    }

    const sbCe = sbRoot.getAttribute('data-safety-briefing-ce') || '';
    const hasValidSbCe = sbCe && sbCe !== '0';
    let safetyLang = '';
    const langHidden = sbRoot.querySelector('input[name="safety_lang"]');
    if (langHidden && langHidden.value) {
        safetyLang = langHidden.value;
    }

    if (!hasValidSbCe && !safetyLang) {
        return;
    }

    // Update page-level context mapping (used for Step 3 form rendered outside wrapper).
    if (safetyLang && hasValidSbCe) {
        contextByLang.set(safetyLang, sbCe);
    }

    // Prefer forms inside the Safety Briefing CE, but also support cases where the Step 3
    // form content element is rendered outside of the CE wrapper (e.g. RECORDS rendering).
    const formsInCe = sbRoot.querySelectorAll('form[id*="SafetyBriefingForm"]');
    const forms = formsInCe.length > 0 ? formsInCe : document.querySelectorAll('form[id*="SafetyBriefingForm"]');

    forms.forEach((form) => {
        const resolvedSbCe = hasValidSbCe ? sbCe : resolveContextCeIdForForm(form, sbRoot);
        if (resolvedSbCe) {
            const input = form.querySelector('input[name*="[sb_ce]"]');
            // Some forms render with a default "0" value; treat that as unset.
            if (input && (!input.value || input.value === '0')) {
                input.value = resolvedSbCe;
            }
        }
        if (safetyLang) {
            const input = form.querySelector('input[name*="[safety_lang]"]');
            if (input && !input.value) {
                input.value = safetyLang;
            }
        }
    });

    // Ensure FriendlyCaptcha language follows selected Safety Briefing language
    // (independent from current TYPO3 site language).
    applyCaptchaLanguage(forms, safetyLang);

    // FriendlyCaptcha auto-initializes on first page load, but Step 3 forms can be injected via AJAX.
    // Re-trigger widget rendering so captcha gets rendered without requiring a full page reload.
    try {
        const roots = sbRoot.querySelectorAll('.frc-captcha')?.length
            ? sbRoot.querySelectorAll('.frc-captcha')
            : document.querySelectorAll('.frc-captcha');
        if (roots && roots.length > 0) {
            // First try v0.1.x attach, with short retries in case footer scripts are still loading.
            tryAttachFriendlyCaptcha(roots);

            // If v0.1.x attach isn't available, try fallbacks for other FriendlyCaptcha builds.
            if (!(window.frcaptcha && typeof window.frcaptcha.attach === 'function')) {
                // Fallbacks for other FriendlyCaptcha builds.
                // 1) Some builds expose a global autoWidget helper.
                const fc = window.friendlyChallenge || window.FriendlyCaptcha || window.friendlycaptcha;
                if (fc && typeof fc.autoWidget === 'function') {
                    fc.autoWidget();
                }

                // 2) If the v2 SDK is available globally, mount widgets programmatically for injected markup.
                const SDK = window.FriendlyCaptchaSDK;
                if (typeof SDK === 'function') {
                    const sdk = new SDK();
                    roots.forEach((el) => {
                        if (!(el instanceof Element)) {
                            return;
                        }
                        if (el.getAttribute('data-fc-mounted') === '1') {
                            return;
                        }
                        // If SDK already rendered into this element, it will usually have children.
                        if (el.children && el.children.length > 0) {
                            el.setAttribute('data-fc-mounted', '1');
                            return;
                        }
                        const sitekey = el.getAttribute('data-sitekey') || '';
                        if (!sitekey) {
                            return;
                        }
                        try {
                            sdk.createWidget({
                                element: el,
                                sitekey,
                                startMode: 'auto',
                            });
                            el.setAttribute('data-fc-mounted', '1');
                        } catch (_) {
                            // ignore
                        }
                    });
                }
            }
        }
    } catch (e) {
        // ignore
    }
}

function installGlobalSafetyBriefingFormHandlers() {
    if (globalListenersInstalled) {
        return;
    }
    globalListenersInstalled = true;

    // Ensure hidden context is set right before actual submission (works even if form is outside CE).
    document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form || !(form instanceof HTMLFormElement)) {
            return;
        }
        if (!form.id || !form.id.includes('SafetyBriefingForm')) {
            return;
        }

        const resolvedSbCe = resolveContextCeIdForForm(form, null);
        if (resolvedSbCe) {
            const input = form.querySelector('input[name*="[sb_ce]"]');
            if (input && (!input.value || input.value === '0')) {
                input.value = resolvedSbCe;
            }
        }
    }, true);

    // Observe DOM changes so newly injected Step 3 forms get context immediately.
    try {
        const obs = new MutationObserver((mutations) => {
            for (const m of mutations) {
                for (const n of m.addedNodes) {
                    if (!(n instanceof Element)) {
                        continue;
                    }
                    const candidates = [];
                    if (n.matches && n.matches('form[id*="SafetyBriefingForm"]')) {
                        candidates.push(n);
                    }
                    n.querySelectorAll && n.querySelectorAll('form[id*="SafetyBriefingForm"]').forEach((f) => candidates.push(f));
                    candidates.forEach((form) => {
                        const resolvedSbCe = resolveContextCeIdForForm(form, null);
                        if (resolvedSbCe) {
                            const input = form.querySelector('input[name*="[sb_ce]"]');
                            if (input && (!input.value || input.value === '0')) {
                                input.value = resolvedSbCe;
                            }
                        }
                    });
                }
            }
        });
        obs.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {
        // ignore
    }
}

async function fetchHtml(url, options = {}) {
    if (!isSameOriginUrl(url)) {
        throw new Error('Cross-origin request blocked');
    }

    const res = await fetch(url, {
        credentials: 'same-origin',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
        },
        ...options,
    });

    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }

    return await res.text();
}

async function fetchJson(url, options = {}) {
    if (!isSameOriginUrl(url)) {
        throw new Error('Cross-origin request blocked');
    }

    const res = await fetch(url, {
        credentials: 'same-origin',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
        },
        ...options,
    });

    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }

    return await res.json();
}

function getStep2JsonUrl(root) {
    try {
        return root?.dataset?.sbStep2JsonUrl ? String(root.dataset.sbStep2JsonUrl) : '';
    } catch (e) {
        return '';
    }
}

function getSbConfigUid(root) {
    try {
        const v = root?.dataset?.sbConfigUid ? String(root.dataset.sbConfigUid) : '';
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    } catch (e) {
        return 0;
    }
}

function updateStep2UiForSection(root, sectionUid, isConfirmed) {
    if (!root || !sectionUid) {
        return;
    }
    const item = root.querySelector(`.wf-safetybriefing__step-2 .accordion-item[data-sb-section-uid="${sectionUid}"]`);
    if (!item) {
        return;
    }
    const pill = item.querySelector('.wf-safetybriefing__step2-status-pill');
    if (pill) {
        const positive = root.querySelector('.wf-safetybriefing__step-2 form[data-sb-step2-form]')?.getAttribute('data-sb-label-positive') || '';
        const negative = root.querySelector('.wf-safetybriefing__step-2 form[data-sb-step2-form]')?.getAttribute('data-sb-label-negative') || '';
        const label = isConfirmed ? positive : negative;
        if (label) {
            pill.textContent = label;
        }
        pill.classList.toggle('button--pill-positive', !!isConfirmed);
        pill.classList.toggle('button--pill-negative', !isConfirmed);
    }

    const input = item.querySelector(`input[name="sb_confirmed_sections[]"][value="${sectionUid}"]`);
    if (input && input instanceof HTMLInputElement) {
        input.checked = !!isConfirmed;
    }
}

function updateStep2UiFromConfirmedIds(root, confirmedIds) {
    if (!root) {
        return;
    }
    const set = new Set((Array.isArray(confirmedIds) ? confirmedIds : []).map((v) => parseInt(v, 10)).filter((n) => Number.isFinite(n)));
    root.querySelectorAll('.wf-safetybriefing__step-2 .accordion-item[data-sb-section-uid]').forEach((item) => {
        const uidStr = item.getAttribute('data-sb-section-uid') || '';
        const uid = parseInt(uidStr, 10);
        if (!Number.isFinite(uid) || uid <= 0) {
            return;
        }
        updateStep2UiForSection(root, uid, set.has(uid));
    });
}

function updateStep2FooterUi(root, progressLabel, allVisibleConfirmed, instructionComplete) {
    if (!root) {
        return;
    }
    const p = root.querySelector('.wf-safetybriefing__step-2 .wf-safetybriefing__step2-progress');
    if (p && typeof progressLabel === 'string' && progressLabel) {
        p.textContent = progressLabel;
    }

    const form = root.querySelector('.wf-safetybriefing__step-2 form[data-sb-step2-form]');
    const cta = root.querySelector('.wf-safetybriefing__step-2 [data-sb-step2-cta="1"]');
    if (cta && cta instanceof HTMLButtonElement) {
        const labelComplete = form?.getAttribute('data-sb-cta-label-complete') || '';
        const labelIncomplete = form?.getAttribute('data-sb-cta-label-incomplete') || '';
        const labelPressed = form?.getAttribute('data-sb-cta-label-pressed') || '';

        if (instructionComplete) {
            cta.disabled = true;
            if (labelPressed) {
                cta.textContent = labelPressed;
            }
            cta.classList.add('button--pill-positive');
            cta.classList.remove('button--pill-negative');
            return;
        }

        cta.disabled = !allVisibleConfirmed;
        if (allVisibleConfirmed) {
            if (labelComplete) {
                cta.textContent = labelComplete;
            }
            cta.classList.add('button--pill-positive');
            cta.classList.remove('button--pill-negative');
        } else {
            if (labelIncomplete) {
                cta.textContent = labelIncomplete;
            }
            cta.classList.add('button--pill-negative');
            cta.classList.remove('button--pill-positive');
        }
    }
}

function getStep3DisabledLabel(root) {
    try {
        return root?.dataset?.sbStep3DisabledLabel ? String(root.dataset.sbStep3DisabledLabel) : '';
    } catch (e) {
        return '';
    }
}

function removeSafetyBriefingStep3FormsEverywhere() {
    try {
        document.querySelectorAll('form[id*="SafetyBriefingForm"]').forEach((form) => {
            if (form && form.parentNode) {
                form.parentNode.removeChild(form);
            }
        });
    } catch (e) {
        // ignore
    }
}

function ensureStep3PlaceholderInCe(root) {
    if (!root) {
        return;
    }
    const step3 = root.querySelector('.wf-safetybriefing__step-3');
    if (!step3) {
        return;
    }

    // Avoid duplicates
    if (step3.querySelector('[data-sb-step3-placeholder="1"]')) {
        return;
    }

    // Step 3 already has a server-rendered disabled CTA. Don't inject another one.
    if (step3.querySelector('.buttongroup')) {
        return;
    }

    const label = getStep3DisabledLabel(root) || '';
    const wrap = document.createElement('div');
    wrap.setAttribute('data-sb-step3-placeholder', '1');
    wrap.className = 'buttongroup';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--pill button--pill-negative';
    btn.disabled = true;
    btn.textContent = label || '—';
    wrap.appendChild(btn);

    const container = step3.querySelector('.container');
    if (container) {
        container.appendChild(wrap);
    } else {
        step3.appendChild(wrap);
    }
}

function removeStep3PlaceholderInCe(root) {
    if (!root) {
        return;
    }
    root.querySelectorAll('.wf-safetybriefing__step-3 [data-sb-step3-placeholder="1"]').forEach((el) => {
        try {
            el.parentNode && el.parentNode.removeChild(el);
        } catch (e) {
            // ignore
        }
    });
}

function hasSafetyBriefingStep3FormForCe(root, ceId) {
    if (!root || !ceId) {
        return false;
    }

    const want = String(ceId);
    const matches = (form) => {
        if (!form || !form.id || !form.id.includes('SafetyBriefingForm')) {
            return false;
        }
        const resolved = resolveContextCeIdForForm(form, root);
        return resolved && String(resolved) === want;
    };

    const inCe = Array.from(root.querySelectorAll('form[id*="SafetyBriefingForm"]')).some(matches);
    if (inCe) {
        return true;
    }

    return Array.from(document.querySelectorAll('form[id*="SafetyBriefingForm"]')).some(matches);
}

function appendStep1SelectionToFormData(root, formData) {
    if (!root || !formData) {
        return;
    }
    const step1Form = root.querySelector('.wf-safetybriefing__step-1 form');
    if (!step1Form) {
        return;
    }

    const role = step1Form.querySelector('select[name="sb_role"]')?.value || '';
    const task = step1Form.querySelector('select[name="sb_task"]')?.value || '';
    if (role) {
        formData.set('sb_role', role);
    }
    if (task) {
        formData.set('sb_task', task);
    }

    // Multi-select checkboxes
    const specials = step1Form.querySelectorAll('input[name="sb_special_properties[]"]');
    specials.forEach((el) => {
        if (el instanceof HTMLInputElement && el.checked && el.value) {
            formData.append('sb_special_properties[]', el.value);
        }
    });
}

class SafetyBriefing {
    constructor(root) {
        this.root = root;
        this.ceId = getRootCeId(root);
        this.isSubmitting = false;
        this.debounceTimer = null;
        this.lastStep2ActiveSectionUid = 0;

        this.bind();
    }

    bind() {
        installGlobalSafetyBriefingFormHandlers();

        // Marker for debugging: indicates JS initialized on this CE.
        try {
            this.root.setAttribute('data-safety-briefing-js', '1');
        } catch (e) {
            // ignore
        }

        // Ensure Step 3 form always has context (AJAX swaps insert new forms).
        fillStep3ContextFields(this.root);

        // Language selector: remove inline onchange (needs cHash URLs) and handle via AJAX.
        this.root.querySelectorAll('.wf-safetybriefing__language select[name="safety_lang"]').forEach((select) => {
            if (select.hasAttribute('onchange')) {
                select.removeAttribute('onchange');
            }
            select.addEventListener('change', () => {
                if (!window.fetch) {
                    // Non-JS fallback already handled by inline onchange; here we just do nothing.
                    return;
                }
                const opt = select.selectedOptions && select.selectedOptions[0] ? select.selectedOptions[0] : null;
                const url = opt && opt.dataset ? (opt.dataset.url || '') : '';
                if (url) {
                    this.submitGetUrlAjax(url);
                } else {
                    // As a fallback, submit the form (might be missing cHash; better than nothing).
                    const form = select.closest('form');
                    if (form) {
                        this.submitGetFormAjax(form);
                    }
                }
            });
        });

        // Intercept Step 1 / Step 2 submits.
        this.root.querySelectorAll('.wf-safetybriefing__step-1 form, .wf-safetybriefing__step-2 form').forEach((form) => {
            form.addEventListener('submit', (e) => {
                if (!window.fetch) {
                    return;
                }
                e.preventDefault();
                const submitter = e.submitter || null;
                this.submitPostFormAjax(form, submitter);
            });
        });

        // Auto-submit Step 1 selects + special property checkbox changes.
        const step1Form = this.root.querySelector('.wf-safetybriefing__step-1 form');
        if (step1Form) {
            step1Form.addEventListener('change', (e) => {
                const t = e.target;
                if (!t || !(t instanceof HTMLElement)) {
                    return;
                }
                if (t.matches('select[name="sb_role"], select[name="sb_task"], input[name="sb_special_properties[]"]')) {
                    this.setStep1Loading(step1Form, true);
                    this.debouncedSubmit(step1Form);
                }
            });
        }

        // Auto-submit Step 2 confirmation checkbox changes.
        const step2Form = this.root.querySelector('.wf-safetybriefing__step-2 form');
        if (step2Form) {
            // On load (and after HTML swaps), sync UI from server-side session state.
            // This avoids “reload shows stale state” when the page is served from TYPO3 cache.
            this.refreshStep2FromServer(step2Form);

            step2Form.addEventListener('change', (e) => {
                const t = e.target;
                if (!t || !(t instanceof HTMLElement)) {
                    return;
                }
                if (t.matches('input[name="sb_confirmed_sections[]"]')) {
                    // Prefer lightweight JSON updates so we don't re-render/collapse the accordion.
                    // The backend endpoint is hardened to ensure FE session persistence.
                    this.lastStep2ActiveSectionUid = getStep2ActiveSectionUidFromEventTarget(t);
                    this.submitStep2ToggleJson(step2Form, t);
                }
            });
        }
    }

    async refreshStep2FromServer(step2Form) {
        const url = getStep2JsonUrl(this.root);
        if (!url || this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        try {
            const formData = new FormData();
            formData.set('sb_get_state', '1');
            formData.set('sb_ce', String(this.ceId));
            const configUid = getSbConfigUid(this.root);
            if (configUid) {
                formData.set('sb_config_uid', String(configUid));
            }
            const safetyLang = step2Form.querySelector('input[name=\"safety_lang\"]')?.value || '';
            formData.set('safety_lang', safetyLang);
            appendStep1SelectionToFormData(this.root, formData);

            const json = await fetchJson(url, { method: 'POST', body: formData });
            if (!json || json.ok !== true) {
                return;
            }

            updateStep2UiFromConfirmedIds(this.root, json.confirmedVisibleIds || []);
            updateStep2FooterUi(this.root, json.progressLabel || '', !!json.allVisibleConfirmed, !!json.instructionComplete);
        } catch (e) {
            // ignore
        } finally {
            this.isSubmitting = false;
        }
    }

    debouncedSubmit(form) {
        if (this.debounceTimer) {
            window.clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = window.setTimeout(() => {
            form.requestSubmit();
        }, 150);
    }

    isStep1Form(form) {
        return !!(form && form.closest && form.closest('.wf-safetybriefing__step-1'));
    }

    setStep1Loading(form, isLoading) {
        if (!this.isStep1Form(form)) {
            return;
        }
        const filters = form.querySelector('.wf-safetybriefing__filters');
        if (filters) {
            filters.classList.toggle('is-loading', !!isLoading);
        }
        form.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    async submitGetFormAjax(form) {
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;

        try {
            const url = new URL(form.action || window.location.href, window.location.href);
            const params = new URLSearchParams(new FormData(form));
            params.forEach((value, key) => url.searchParams.set(key, value));

            // Drop cHash – TYPO3 will generate a new one as needed.
            url.searchParams.delete('cHash');

            const html = await fetchHtml(url.toString(), { method: 'GET' });
            this.swapFromHtml(html);
        } catch (e) {
            // Fallback: hard navigation if anything goes wrong.
            try {
                form.submit();
            } catch (_) {
                // ignore
            }
        } finally {
            this.isSubmitting = false;
        }
    }

    async submitGetUrlAjax(url) {
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;

        try {
            const html = await fetchHtml(url, { method: 'GET' });
            this.swapFromHtml(html);
        } catch (e) {
            // Fallback: hard navigation.
            try {
                window.location.href = url;
            } catch (_) {
                // ignore
            }
        } finally {
            this.isSubmitting = false;
        }
    }

    async submitPostFormAjax(form, submitter, extra = null) {
        if (this.isSubmitting) {
            return;
        }
        const isStep1Form = this.isStep1Form(form);
        if (isStep1Form) {
            this.setStep1Loading(form, true);
        }
        this.isSubmitting = true;

        try {
            const isStep2Form = !!(form && form.closest && form.closest('.wf-safetybriefing__step-2'));
            const isCompleteSubmit = !!(submitter && submitter instanceof HTMLButtonElement && submitter.name === 'sb_mark_complete');

            const hasAnchorOverride = !!(extra && Object.prototype.hasOwnProperty.call(extra, 'activeAnchorOverride'));
            // Preserve accordion context + scroll position for Step 2 checkbox interactions.
            const activeAnchor = hasAnchorOverride
                ? extra.activeAnchorOverride
                : ((!isCompleteSubmit && this.lastStep2ActiveSectionUid)
                    ? captureAnchorPosition(this.root, this.lastStep2ActiveSectionUid)
                    : null);

            const actionUrl = form.action || window.location.href;
            const formData = serializeFormSubmit(form, submitter);

            const html = await fetchHtml(actionUrl, {
                method: 'POST',
                body: formData,
            });

            const hasScrollOverride = !!(extra && Object.prototype.hasOwnProperty.call(extra, 'scrollToStep3After'));
            const scrollToStep3After = hasScrollOverride
                ? !!extra.scrollToStep3After
                : (isStep2Form && isCompleteSubmit);

            this.swapFromHtml(html, activeAnchor, {
                scrollToStep3After,
            });
        } catch (e) {
            // Fallback: regular submit.
            try {
                form.submit();
            } catch (_) {
                // ignore
            }
        } finally {
            this.isSubmitting = false;
            if (isStep1Form) {
                this.setStep1Loading(form, false);
            }
        }
    }

    async submitStep2ToggleJson(form, checkboxEl) {
        if (this.isSubmitting) {
            return;
        }

        const url = getStep2JsonUrl(this.root);
        if (!url) {
            // No endpoint configured -> fallback to existing behavior.
            this.debouncedSubmit(form);
            return;
        }

        const sectionUid = getStep2ActiveSectionUidFromEventTarget(checkboxEl);
        if (!sectionUid) {
            this.debouncedSubmit(form);
            return;
        }

        const isChecked = checkboxEl instanceof HTMLInputElement ? checkboxEl.checked : false;

        // Optimistic UI update
        updateStep2UiForSection(this.root, sectionUid, isChecked);

        let json = null;
        this.isSubmitting = true;
        try {
            const formData = new FormData();
            formData.set('sb_ce', String(this.ceId));
            const configUid = getSbConfigUid(this.root);
            if (configUid) {
                formData.set('sb_config_uid', String(configUid));
            }
            const safetyLang = form.querySelector('input[name="safety_lang"]')?.value || '';
            formData.set('safety_lang', safetyLang);
            appendStep1SelectionToFormData(this.root, formData);

            // Send only the toggle (server applies against current state + visible set).
            formData.set('toggle_section_uid', String(sectionUid));
            formData.set('toggle_checked', isChecked ? '1' : '0');

            json = await fetchJson(url, { method: 'POST', body: formData });
            if (!json || json.ok !== true) {
                throw new Error('JSON endpoint returned error');
            }

            // Apply server truth across the whole step 2 UI.
            updateStep2UiFromConfirmedIds(this.root, json.confirmedVisibleIds || []);
            updateStep2FooterUi(this.root, json.progressLabel || '', !!json.allVisibleConfirmed, !!json.instructionComplete);
        } catch (e) {
            // Fallback: use existing HTML-swap submit if JSON fails.
            try {
                this.debouncedSubmit(form);
            } catch (_) {
                // ignore
            }
            return;
        } finally {
            this.isSubmitting = false;
        }

        // Step 3 visibility may require a server-rendered HTML refresh; run outside the JSON submit lock
        // so nested `submitPostFormAjax` calls are not blocked.
        if (json) {
            await this.applyStep3VisibilityFromJson(json, form);
        }
    }

    async applyStep3VisibilityFromJson(json, step2Form) {
        if (!json) {
            return;
        }

        const show = !!json.showStep3Form;

        if (show) {
            removeStep3PlaceholderInCe(this.root);

            const hasForm = hasSafetyBriefingStep3FormForCe(this.root, this.ceId);
            if (!hasForm && step2Form) {
                const activeAnchor = this.lastStep2ActiveSectionUid
                    ? captureAnchorPosition(this.root, this.lastStep2ActiveSectionUid)
                    : null;

                // Server-rendered Step 3 markup (fresh/empty) without relying on a full page reload.
                await this.submitPostFormAjax(step2Form, null, {
                    scrollToStep3After: !!json.allVisibleConfirmed,
                    activeAnchorOverride: activeAnchor,
                });
            }

            // Step 3 can become visible without an HTML swap (form already present but previously hidden).
            // In that case FriendlyCaptcha won't auto-mount, so re-apply context + trigger widget attach.
            fillStep3ContextFields(this.root);
            return;
        }

        // Step 2 incomplete again: remove Step 3 forms from the page.
        removeSafetyBriefingStep3FormsEverywhere();
        removeStep3PlaceholderInCe(this.root);
        ensureStep3PlaceholderInCe(this.root);
    }

    swapFromHtml(html, activeAnchor = null, options = {}) {
        const doc = parseHtml(html);
        const nextRoot = findMatchingRoot(doc, this.ceId);
        if (!nextRoot) {
            throw new Error('Safety Briefing root not found in response');
        }

        const replaced = replaceRoot(this.root, nextRoot);
        if (!replaced) {
            throw new Error('Replace failed');
        }

        // Re-init on the freshly inserted markup.
        this.root = replaced;
        this.bind();

        this.root.querySelectorAll('.accordion').forEach((node) => {
            new Accordion(node);
        });

        // If the user confirmed a section in Step 2, keep that section open and keep the viewport stable.
        if (activeAnchor) {
            restoreAnchorPositionAndOpen(this.root, activeAnchor);
        }

        // After completing Step 2, bring the user to Step 3 (headline) instead of leaving them at the footer.
        if (options && options.scrollToStep3After) {
            // Let layout settle after DOM replacement + accordion init.
            window.requestAnimationFrame(() => {
                scrollToStep3Headline(this.root);
            });
        }
    }
}

window.app.run(() => {
    document.querySelectorAll(SELECTOR_ROOT).forEach((node) => {
        new SafetyBriefing(node);
    });
});

